import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { usePowtoonAuth } from "@/lib/auth/PowtoonAuthContext";
import { getRedirectUri } from "@/lib/powtoon-oauth";
import { consumePostLoginDestination } from "@/lib/auth/postLoginDestination";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { trackEvent } from "@/services/analytics";
import { ANALYTICS_CATEGORIES, ANALYTICS_ACTIONS } from "@/config/analytics-config";
import { createLogger } from "@/lib/frontend-logger";

const logger = createLogger("auth.ProcessAuthCode");

function readCookie(name: string): string | null {
  return (
    document.cookie.split("; ").find((c) => c.startsWith(`${name}=`))?.split("=")[1] ?? null
  );
}

export function ProcessAuthCodePage() {
  useDocumentMeta({ title: "Signing in… — Powtoon Live Avatar" });
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { installSession } = usePowtoonAuth();
  const [error, setError] = useState<string | null>(null);
  const exchangedRef = useRef(false);

  useEffect(() => {
    const code = params.get("code");
    const state = params.get("state");
    if (!code) {
      setError("Missing authorization code");
      logger.warn("auth callback missing code");
      return;
    }
    const expected = readCookie("oauth_state");
    if (expected && state && expected !== state) {
      setError("Invalid OAuth state");
      logger.error("auth state mismatch");
      trackEvent({ category: ANALYTICS_CATEGORIES.AUTH, action: ANALYTICS_ACTIONS.ERROR, ga4EventName: "auth_error", label: "state_mismatch" });
      return;
    }
    const codeFlagKey = `oauth_code_processed:${code}`;
    if (window.sessionStorage.getItem(codeFlagKey)) {
      const dest = consumePostLoginDestination();
      navigate(dest.to, { replace: dest.replace });
      return;
    }
    if (exchangedRef.current) return;
    exchangedRef.current = true;
    void (async () => {
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
        const res = await fetch(`${supabaseUrl}/functions/v1/powtoon-auth`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code, redirect_uri: getRedirectUri() }),
        });
        const data = await res.json();
        if (!res.ok) {
          setError(data?.error || `Auth failed (${res.status})`);
          logger.error("auth code exchange failed", { status: res.status, error: data?.error });
          trackEvent({ category: ANALYTICS_CATEGORIES.AUTH, action: ANALYTICS_ACTIONS.ERROR, ga4EventName: "auth_error", label: `status_${res.status}` });
          return;
        }
        installSession({
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          expires_in: data.expires_in,
          user: {
            powtoonUserId: data.powtoon_user_id,
            email: data.email,
            displayName: data.display_name,
            avatarUrl: data.avatar_url,
            permissions: {
              canView: !!data.permissions?.can_view,
              canEdit: !!data.permissions?.can_edit,
            },
          },
        });
        window.sessionStorage.setItem(codeFlagKey, "1");
        document.cookie = "oauth_state=; path=/; max-age=0";
        const dest = consumePostLoginDestination();
        navigate(dest.to, { replace: dest.replace });
      } catch (e) {
        setError(e instanceof Error ? e.message : "Unknown error");
        logger.error("auth code exchange threw", { error: e instanceof Error ? e.message : String(e) });
        trackEvent({ category: ANALYTICS_CATEGORIES.AUTH, action: ANALYTICS_ACTIONS.ERROR, ga4EventName: "auth_error", label: "exception" });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-6">
      <div className="max-w-md text-center">
        {!error ? (
          <>
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
            <h1 className="mt-4 font-display text-xl font-semibold">Signing you in…</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Completing your Powtoon authentication.
            </p>
          </>
        ) : (
          <>
            <h1 className="font-display text-xl font-semibold text-destructive">
              Sign-in failed
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">{error}</p>
            <button
              className="mt-6 inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              onClick={() => navigate("/", { replace: true })}
            >
              Back to home
            </button>
          </>
        )}
      </div>
    </main>
  );
}
